import { SavedDataDetailsProps } from 'interfaces/components/savedUserData';
import { companyStatus } from 'utils/constants';
import {
  formatValue,
  formatCurrency,
  formatPercentage,
  formatRatio,
  formatDateForHeader
} from 'utils/helpers/dataFormatters';
import DataRow from './DataRow';
import DataSection from './DataSection';
import MultiValueRow from './MultiValueRow';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import { formatUserHistoricalFormData } from 'utils/helpers/formatUserHistoricalFormData';
import { useAuth } from 'context/AuthContext';

const SavedDataDetails = ({
  data,
  onClose,
  onDelete,
  onApply
}: SavedDataDetailsProps) => {
  const { companyName } = useAuth();

  const companyStatusText =
    data?.STATUS === companyStatus.PRIVATE ? '(Private)' : '(Public)';
  const companyWebsite = data?.COMPANY_WEBSITE;
  const formattedData = formatUserHistoricalFormData(data);

  const onApplyData = () => {
    onApply?.(data);
  };

  const renderCompanyInfo = () => {
    if (companyName) {
      return `${companyName} ${companyStatusText} ${
        companyWebsite ? `| ${companyWebsite}` : ''
      }`;
    } else if (companyWebsite) {
      return `${companyWebsite} ${companyStatusText}`;
    } else {
      return 'Your Information';
    }
  };

  return (
    <div className='w-full h-full flex flex-col'>
      <ModalHeader
        title={formatDateForHeader(data.DW_LAST_UPDT)}
        description='Check your saved data below.'
        onClose={onClose}
      />

      {/* Main Content */}
      <div className='flex flex-col gap-4 px-8 py-6 border-y border-gray-300 dark:border-gray-700 flex-1 overflow-auto'>
        {/* Company Name */}
        <h3 className='text-2xl dark:text-white'>{renderCompanyInfo()}</h3>
        <div className='grid grid-cols-4 h-full border border-gray-200 dark:border-gray-700 p-4 rounded-lg w-[896px]'>
          {/* Growth and Retention */}
          <DataSection
            title='Growth and Retention'
            className='pr-4 border-r border-gray-200 dark:border-gray-700'
          >
            <DataRow
              label='YoY ARR Growth'
              value={formattedData?.YOY_ARR_OR_REVENUE_GROWTH}
              formatFunction={formatPercentage}
            />
            <DataRow
              label='New Logo ARR from Gross New'
              value={formattedData?.NEWLOGOARROFGROSSNEW}
              formatFunction={formatCurrency}
            />
            <DataRow
              label='Expansion ARR from Gross New'
              value={formattedData?.EXPANSIONARROFGROSSNEW}
              formatFunction={formatCurrency}
            />
            <DataRow
              label='New Logo Velocity'
              value={formattedData?.NEWLOGOVELOCITY}
              formatFunction={formatPercentage}
            />
            <DataRow
              label='Quick Ratio'
              value={formattedData?.QUICK_RATIO}
              formatFunction={formatRatio}
            />
            <DataRow
              label='Net Dollar Retention'
              value={formattedData?.NET_DOLLAR_RETENTION}
              formatFunction={formatPercentage}
            />
            <DataRow
              label='Gross Dollar Retention'
              value={formattedData?.GROSSDOLLARRETENTION}
              formatFunction={formatPercentage}
            />
            <DataRow
              label='Current ARR or Annualized Revenue'
              value={formattedData?.CURRENT_ARR_OR_ANNUALIZED_REVENUE}
              formatFunction={formatCurrency}
            />
            <DataRow
              label='Revenue'
              value={formattedData?.REVENUE}
              formatFunction={formatCurrency}
            />
            <DataRow
              label='Total Headcount'
              value={formattedData?.TOTAL_HEADCOUNT}
              formatFunction={formatValue}
            />
            <DataRow
              label='Avg Deal Size'
              value={formattedData?.AVGDEALSIZE}
              formatFunction={formatCurrency}
            />
          </DataSection>

          {/* Operational Efficiency */}
          <DataSection
            title='Operational Efficiency'
            className='px-4 border-r border-gray-200 dark:border-gray-700'
          >
            <DataRow
              label='Net Magic Number'
              value={formattedData?.NET_MAGIC_NUMBER}
              formatFunction={formatRatio}
            />
            <DataRow
              label='Rule of 40'
              value={formattedData?.RULE_OF_40}
              formatFunction={formatPercentage}
            />
            <DataRow
              label='CAC Payback Period'
              value={formattedData?.CACPAYBACK}
              formatFunction={value => formatValue(value, ' months')}
            />
            <DataRow
              label='Burn Multiple'
              value={formattedData?.BURN_MULTIPLE}
              formatFunction={formatRatio}
            />
            <MultiValueRow
              label='OpEx as a % of Revenue'
              values={[
                {
                  key: 'G&A',
                  value:
                    formattedData?.OPEX_FUNCTION_AS_A_PERCENTAGE_OF_REVENUE_G_AND_A
                },
                {
                  key: 'R&D',
                  value:
                    formattedData?.OPEX_FUNCTION_AS_A_PERCENTAGE_OF_REVENUE_R_AND_D
                },
                {
                  key: 'S&M',
                  value:
                    formattedData?.OPEX_FUNCTION_AS_A_PERCENTAGE_OF_REVENUE_S_AND_M
                }
              ]}
              formatFunction={formatPercentage}
            />
          </DataSection>

          {/* Profitability and Cash Flow */}
          <DataSection
            title='Profitability and Cash Flow'
            className='px-4 border-r border-gray-200 dark:border-gray-700'
          >
            <DataRow
              label='Gross Margin'
              value={formattedData?.GROSS_MARGIN}
              formatFunction={formatPercentage}
            />
            <DataRow
              label='FCF Margin'
              value={formattedData?.FCF_MARGIN}
              formatFunction={formatPercentage}
            />
          </DataSection>

          {/* Headcount */}
          <DataSection title='Headcount' className='pl-4'>
            <MultiValueRow
              label='Headcount Distribution (Total FTEs)'
              values={[
                { key: 'G&A', value: formattedData?.HEADCOUNT_PER_G_AND_A },
                { key: 'R&D', value: formattedData?.HEADCOUNT_PER_R_AND_D },
                { key: 'S&M', value: formattedData?.HEADCOUNT_PER_S_AND_M }
              ]}
              formatFunction={formatPercentage}
            />
            <DataRow
              label='ARR per FTE'
              value={formattedData?.ARR_PER_FTE}
              formatFunction={formatCurrency}
            />
            <DataRow
              label='OpEx per FTE'
              value={formattedData?.OPEXPERFTE}
              formatFunction={formatCurrency}
            />
          </DataSection>
        </div>
      </div>

      <ModalFooter onDelete={onDelete} onApply={onApplyData} />
    </div>
  );
};

export default SavedDataDetails;
